import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ActivitySquare,
  ArrowRight,
  Bell,
  CheckCircle2,
  Circle,
  Globe,
  Lock,
  Sparkles,
  Users,
  X,
} from "lucide-react";
import { Card } from "@tremor/react";
import { useAuthStore } from "../../store/authStore.js";

/**
 * Persisted per-workspace dismissal flag (localStorage). Returns
 * `[dismissed, dismiss]`. Dismissing one workspace's card doesn't affect others.
 */
function useDismissed(name) {
  const workspaceId = useAuthStore((s) => s.workspace?.id) ?? "anon";
  const storageKey = `dashboard-dismiss-${name}:${workspaceId}`;
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(storageKey) === "true";
    } catch {
      return false;
    }
  });
  const dismiss = () => {
    try {
      localStorage.setItem(storageKey, "true");
    } catch {
      /* ignore storage failures */
    }
    setDismissed(true);
  };
  return [dismissed, dismiss];
}

function DismissButton({ onClick, label = "Dismiss" }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      className="shrink-0 rounded p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
    >
      <X className="h-4 w-4" />
    </button>
  );
}

/**
 * Derive the single most useful "do this next" suggestion from the current
 * workspace state. Deterministic — same inputs, same suggestion.
 */
export function recommendNextMonitor({ monitors }) {
  const active = monitors.filter((m) => m.is_active);
  const types = new Set(monitors.map((m) => m.type));

  // Suggest SSL for an HTTP target that isn't already SSL-monitored.
  const httpMon = monitors.find(
    (m) =>
      (m.type === "http" || m.type === "api") && /^https:\/\//i.test(m.target || "")
  );
  if (httpMon && !types.has("ssl")) {
    let host = httpMon.target;
    try {
      host = new URL(httpMon.target).hostname;
    } catch {
      /* keep raw target */
    }
    return {
      key: "ssl",
      title: `Monitor SSL expiry for ${host}`,
      detail:
        "You're already checking this site over HTTPS — add an SSL monitor so you're warned before the certificate expires.",
      cta: "Add SSL monitor",
      to: `/monitors?new=ssl&target=${encodeURIComponent(host)}`,
    };
  }

  // Has HTTP but no browser/transaction check.
  if (types.has("http") && !types.has("playwright")) {
    return {
      key: "playwright",
      title: "Add a browser journey check",
      detail:
        "Catch broken logins and checkouts that a simple HTTP ping can't see — record a real browser flow.",
      cta: "Add browser check",
      to: "/monitors?new=playwright",
    };
  }

  // Has monitors but none in a second location.
  if (active.length > 0 && active.every((m) => (m.locations?.length || 0) <= 1)) {
    return {
      key: "multiregion",
      title: "Check from a second region",
      detail:
        "A single probe location can't tell a real outage from a regional network blip. Add another location to confirm failures.",
      cta: "Review locations",
      to: `/monitors/${active[0].id}`,
    };
  }

  // Healthy baseline — nudge toward page speed.
  if (!types.has("page_speed")) {
    return {
      key: "page_speed",
      title: "Track Core Web Vitals",
      detail:
        "Add a page-speed monitor to watch LCP, CLS and INP trends and catch performance regressions after deploys.",
      cta: "Add page-speed monitor",
      to: "/monitors?new=page_speed",
    };
  }
  return null;
}

function ChecklistItem({ done, Icon, title, detail, to, cta }) {
  return (
    <div className="flex items-start gap-3 py-3">
      {done ? (
        <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-500" />
      ) : (
        <Circle className="mt-0.5 h-5 w-5 shrink-0 text-gray-300" />
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-gray-400" />
          <p
            className={`text-sm font-medium ${
              done ? "text-gray-400 line-through" : "text-gray-800"
            }`}
          >
            {title}
          </p>
        </div>
        {!done && <p className="mt-0.5 text-xs text-gray-500">{detail}</p>}
      </div>
      {!done && (
        <Link
          to={to}
          className="shrink-0 whitespace-nowrap text-xs font-medium text-teal-600 hover:text-teal-700"
        >
          {cta} →
        </Link>
      )}
    </div>
  );
}

/**
 * Setup-progress checklist. Hidden once every step is complete.
 */
export function SetupProgress({
  monitorCount,
  alertingReady,
  hasStatusPage,
  memberCount,
}) {
  const [dismissed, dismiss] = useDismissed("setup");
  const items = [
    {
      key: "monitor",
      done: monitorCount > 0,
      Icon: ActivitySquare,
      title: "Add your first monitor",
      detail: "Start watching a URL, host, or service.",
      to: "/wizard",
      cta: "Smart Setup",
    },
    {
      key: "alerting",
      done: alertingReady,
      Icon: Bell,
      title: "Set up alerting",
      detail: "Create a channel so the right people get notified.",
      to: "/channels",
      cta: "Configure",
    },
    {
      key: "status",
      done: hasStatusPage,
      Icon: Globe,
      title: "Publish a status page",
      detail: "Keep customers informed during incidents.",
      to: "/status-pages",
      cta: "Create",
    },
    {
      key: "team",
      done: memberCount > 1,
      Icon: Users,
      title: "Invite your team",
      detail: "Share monitoring and on-call duties.",
      to: "/team",
      cta: "Invite",
    },
  ];

  const completed = items.filter((i) => i.done).length;
  if (dismissed || completed === items.length) return null;

  const pct = Math.round((completed / items.length) * 100);

  return (
    <Card className="p-0">
      <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
        <h2 className="text-sm font-semibold text-gray-700">Finish setting up</h2>
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-gray-500">
            {completed} of {items.length} done
          </span>
          <DismissButton onClick={dismiss} label="Dismiss setup checklist" />
        </div>
      </div>
      <div className="px-5 pt-4">
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
          <div
            className="h-1.5 rounded-full bg-teal-500 transition-all"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>
      <div className="divide-y divide-gray-100 px-5 pb-3">
        {items.map((item) => (
          <ChecklistItem key={item.key} {...item} />
        ))}
      </div>
    </Card>
  );
}

/**
 * Recommended-next-monitor nudge. Renders nothing when no suggestion fits.
 */
export function RecommendedMonitor({ monitors }) {
  const [dismissed, dismiss] = useDismissed("recommended");
  const rec = recommendNextMonitor({ monitors });
  if (dismissed || !rec) return null;
  return (
    <Card className="border-teal-100 bg-gradient-to-br from-teal-50 to-white">
      <div className="flex items-start gap-3">
        <div className="rounded-lg bg-teal-100 p-2">
          <Sparkles className="h-5 w-5 text-teal-600" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold uppercase tracking-wide text-teal-700">
            Recommended next
          </p>
          <p className="mt-1 text-sm font-semibold text-gray-900">{rec.title}</p>
          <p className="mt-1 text-xs text-gray-600">{rec.detail}</p>
          <Link
            to={rec.to}
            className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-teal-700 hover:text-teal-800"
          >
            {rec.cta} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <DismissButton onClick={dismiss} label="Dismiss recommendation" />
      </div>
    </Card>
  );
}

/**
 * Full-page first-run hero shown when the workspace has zero monitors.
 */
export function FirstRunHero() {
  const tiles = [
    { Icon: ActivitySquare, label: "Uptime & response time" },
    { Icon: Lock, label: "SSL & domain expiry" },
    { Icon: Globe, label: "Browser & API journeys" },
  ];
  return (
    <div className="mx-auto max-w-2xl py-10 text-center">
      <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-teal-100">
        <ActivitySquare className="h-7 w-7 text-teal-600" />
      </div>
      <h1 className="text-2xl font-bold text-gray-900">
        Let's set up your first monitor
      </h1>
      <p className="mx-auto mt-2 max-w-md text-sm text-gray-500">
        Monitero watches your sites, APIs, and servers and tells you the moment
        something breaks — with the context to fix it fast.
      </p>

      <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
        <Link
          to="/wizard"
          className="inline-flex items-center gap-2 rounded-lg bg-teal-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-teal-700"
        >
          <Sparkles className="h-4 w-4" /> Start Smart Setup
        </Link>
        <Link
          to="/monitors?new=http"
          className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-5 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
        >
          Add a monitor manually
        </Link>
      </div>

      <div className="mt-10 grid grid-cols-1 gap-3 sm:grid-cols-3">
        {tiles.map(({ Icon, label }) => (
          <div
            key={label}
            className="rounded-xl border border-gray-200 bg-white px-4 py-5 text-center"
          >
            <Icon className="mx-auto h-6 w-6 text-teal-500" />
            <p className="mt-2 text-xs font-medium text-gray-600">{label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
